import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";

/**
 * DefaultFormNumberInputBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
  value: string;
}

const DefaultFormNumberInputBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, value, ...other } = props;
  return (
    <IMaskInput
      {...other}
      inputRef={refs}
      value={value ?? null}
      onKeyDown={e => {

        // Permite atalhos como Ctrl+C, Ctrl+V, Ctrl+A, Ctrl+X
        if (e.ctrlKey || e.metaKey) {
          return;
        }

        // Permite teclas de controle/navegação
        const allowedKeys = [
          'Backspace',
          'Delete',
          'Tab',
          'ArrowLeft',
          'ArrowRight',
          'Home',
          'End',
        ];

        if (allowedKeys.includes(e.key)) {
          return;
        }

        // Impede qualquer coisa que não seja um dígito
        if (!/^\d$/.test(e.key)) {
          e.preventDefault();
        }
      }}
      onAccept={(newValue: any, { unmaskedValue }: { value: string, unmaskedValue: string }) => {
        if (unmaskedValue.length > 0) {
          onChange({ target: { name: props.name, value: newValue } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={true}
    />
  );
});

/**
 * DefaultFormNumericInput
 */

type Props<T extends {}> = {
  name: keyof T;
  label?: string;
  required?: boolean;
  readOnly?: boolean;
  reactHookForm: UseFormReturn<T>;
};

export const DefaultFormNumericInput = <T extends {},>({
  name,
  label,
  required,
  readOnly,
  reactHookForm,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let value = '';

        let helperText: string | null = null;
        let shrink = false;

        if (typeof field.value === 'string') {
          value = field.value;
        }
        else if (typeof field.value === 'number') {
          value = (Math.round((field.value + Number.EPSILON) * 100)).toString();
        }
        else if (field.value === null) {
          value = '';
        }

        if (field.value !== undefined && field.value !== null) {
          if (typeof field.value === 'string') {
            if (field.value.length > 0) {
              shrink = true;
            }
          } else {
            shrink = true;
          }
        }

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <FormControl
            sx={{ width: '100%', minHeight: 45 }}
            variant="outlined"
            size="small">
            <InputLabel
              htmlFor={`form-input__${name as string}`}
              shrink={shrink}
              required={required}
              error={helperText !== null}>
              {label}
            </InputLabel>

            <OutlinedInput
              {...field}
              size="small"
              label={(value && value.length > 0) ? label : null}
              value={value ?? null}
              notched={true}
              fullWidth={true}
              readOnly={readOnly}
              onChange={e => {
                if (!readOnly) {
                  if (e.target.value !== null && e.target.value.length > 0 && !isNaN(Number(e.target.value))) {
                    reactHookForm.setValue(name as any, (Number(e.target.value) / 100) as any);
                  } else {
                    reactHookForm.setValue(name as any, null as any);
                  }
                }
              }}
              inputComponent={DefaultFormNumberInputBase as any}
              inputProps={{
                mask: [
                  { mask: '[#]\\0,\\00', definitions: { '#': /[-]/ } },
                  { mask: '[#]\\0,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]0,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]00,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]000,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]0.000,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]00.000,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]000.000,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]0.000.000,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]00.000.000,00', definitions: { '#': /[-]/ } },
                  { mask: '[#]000.000.000,00', definitions: { '#': /[-]/ } },
                ],
                placeholder: "___.___.___,__",
              }}
            />

            <FormHelperText id={`${name as string}-component-error-text`} error={helperText !== null && helperText !== undefined}>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}